import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';

// ─── Site-wide defaults ──────────────────────────────────────────────────
// ASSUMPTION: SITE_URL and DEFAULT_IMAGE are best-guesses — confirm/adjust
// against the real production domain and an actual default social-share
// image that exists at that path.
const SITE_NAME = 'My Couponbook';
export const SITE_URL = 'https://www.mycouponbook.ca';
const DEFAULT_TITLE = "My Couponbook | Canada's Coupon app";
const DEFAULT_DESCRIPTION = 'Discover the latest deals and coupons from local businesses across Canada.';
const DEFAULT_IMAGE = 'https://www.mycouponbook.ca/static/media/MyCouponLogo.bab0e1315d16b086f0749daa1a252939.svg';

// og:image / twitter:image must be an absolute URL — most crawlers
// (Facebook's especially) won't resolve a relative path correctly. Leaves
// already-absolute (http/https) and data URIs untouched.
const toAbsoluteUrl = path => {
  if (!path) return null;
  if (/^(https?:)?\/\//i.test(path) || path.startsWith('data:')) return path;
  return `${SITE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
};

/**
 * Shared SEO/social head tags for customer-facing pages: title,
 * description, canonical, Open Graph, Twitter Card, and optional JSON-LD
 * structured data — one place instead of a hand-rolled <Helmet> per page.
 *
 * USAGE:
 *   <SEOHead
 *     title="Half Off Lattes at Java House"   // page-specific only — " | MyCouponbook" is appended automatically
 *     description="Save 50% on any latte at Java House in Halifax. Redeem in-app before Aug 30."
 *     image={resolvedHeroImage}                // relative or absolute — resolved to absolute automatically
 *     path={`/coupons/${coupon.id}`}           // used for canonical + og:url
 *     type="product"                           // 'website' | 'article' | 'product' | 'profile'
 *     structuredData={{ '@context': 'https://schema.org', '@type': 'Offer', ... }}
 *   />
 *
 * Omit any prop to fall back to sensible site-wide defaults — a page that
 * only passes `title` still gets a complete, valid set of tags.
 */
const SeoHead = ({ title, description, image, path, type, noIndex, structuredData }) => {
  const resolvedTitle = title ? `${title}` : DEFAULT_TITLE;
  const resolvedDescription = description || DEFAULT_DESCRIPTION;
  const resolvedImage = toAbsoluteUrl(image) || DEFAULT_IMAGE;
  const resolvedPath = path || (typeof window !== 'undefined' ? window.location.pathname : '');
  const resolvedUrl = `${SITE_URL}${resolvedPath}`;

  const jsonLdBlocks = Array.isArray(structuredData) ? structuredData : structuredData ? [structuredData] : [];

  return (
    <Helmet>
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <link rel="canonical" href={resolvedUrl} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:image" content={resolvedImage} />
      <meta property="og:url" content={resolvedUrl} />

      {/* Twitter Card — no twitter:site since there's no account to attribute to;
          these tags still drive link previews in Slack/Discord/iMessage etc. */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={resolvedImage} />

      {/* Structured data — pass one object or an array of them */}
      {jsonLdBlocks.map((block, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <script key={index} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
};

SeoHead.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
  path: PropTypes.string,
  type: PropTypes.oneOf(['website', 'article', 'product', 'profile']),
  noIndex: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  structuredData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

SeoHead.defaultProps = {
  title: undefined,
  description: undefined,
  image: undefined,
  path: undefined,
  type: 'website',
  noIndex: false,
  structuredData: undefined,
};

export default SeoHead;